import type { MemberClubView, PrimaryTokenView, ProjectedSlotView } from '../lib/lookup-types';

/** rules/rule8.txt: the admin-set senior sponsor check limit and the fixed rule constants. */
export interface PlacementRulesView {
  uplineSearchLimit: number;
  downlineBranches: number;
  downlineDepth: number;
  downlineCheckBudget: number;
  qualifyingDirects: number;
  defaultUplineSearchLimit: number;
  updatedAt: string | null;
}

export const DEFAULT_PLACEMENT_RULES: PlacementRulesView = {
  uplineSearchLimit: 36,
  downlineBranches: 6,
  downlineDepth: 6,
  downlineCheckBudget: 36,
  qualifyingDirects: 2,
  defaultUplineSearchLimit: 36,
  updatedAt: null,
};

export type Era = 'STANDARD' | 'HALF' | 'QUARTER';
export type NodeKind = 'you' | 'income' | 'upline' | 'downline' | 'treasury' | 'recycle' | 'empty';

export interface MatrixNode {
  position: number;
  memberId?: string;
  kind: NodeKind;
  state?: 'confirmed' | 'pending' | 'empty';
}

export interface SlotSummaryView {
  number: number;
  label: string;
  price: string;
  /** Activated by the contract (indexed slot activation / SeatFilled); nothing is drawn from the placement rules. */
  active: boolean;
  virtual?: boolean;
  cycle: number;
  placements: number;
  received: number;
  recycled: number;
  earning: string;
  progress: number;
  status: 'locked' | 'active' | 'completed';
}

export interface ActivityRecord {
  id: string;
  type: 'JOIN' | 'SPONSOR' | 'PLACEMENT' | 'UPGRADE' | 'RECYCLE' | 'TOKEN' | 'REDEMPTION' | 'SYSTEM';
  memberId: string;
  amount: string;
  status: 'Confirmed' | 'Pending' | 'Failed' | 'Reorged';
  timestamp: string;
  hash: string;
  blockNumber: number | null;
}

export interface MemberView {
  id: string;
  wallet: string;
  sponsorId: string | null;
  era: Era;
  joinedAt: string;
  activeSlot: number;
  currentCycle: number;
  rank: string;
  status: 'pending' | 'active' | 'suspended';
}

export interface PartnerView {
  id: string;
  era: Era;
  joined: string;
  activeSlot: number;
  volume: string;
  status: 'pending' | 'active' | 'suspended';
}

export interface GenerationNodeView {
  id: string;
  parent: string | null;
  depth: number;
  volume: string;
}

export interface TokenView {
  tokenId: string;
  memberId: string;
  state: string;
  era: string;
  ratio: string;
  issued: string;
  unlocks: string | null;
  progress: number;
  providerCode: string | null;
}

export interface ProviderViewModel {
  code: string;
  name: string;
  icon: string;
  multiplier: string;
  model: string;
  eras: string[];
  active: boolean;
  governanceStatus: 'approved' | 'review' | 'suspended';
}

export interface RankViewModel {
  rank: string;
  level: number;
  target: number;
  current: number;
  share: string;
  unlocked: boolean;
  color: string;
  distributedAmount: string;
  nextDistributionAt: string;
}

export interface RpcProviderViewModel {
  id: string;
  name: string;
  state: 'healthy' | 'degraded';
  latency: number | null;
  traffic: number;
  priority: number;
  failures: number;
  lastCheckedAt: string | null;
}

export interface CyclePlacementView {
  positionNumber: number;
  memberBdId: string;
  placementType: 'direct' | 'spillover' | 'virtual';
  amountAtomic: string;
  txHash: string | null;
  createdAt: string;
  /** On-chain SeatFilled kind (sponsor_one, slot_owner, upgrade_hold, recycle, ...). */
  kind?: string;
}

export interface CycleView {
  slotNumber: number;
  cycleNumber: number;
  status: 'active' | 'completed' | 'paused';
  totalPositions: number;
  completedPositions: number;
  startedAt: string;
  completedAt: string | null;
  placements: CyclePlacementView[];
}

export interface DashboardStatsView {
  teamMembers: number;
  directMembers: number;
  completedCycles: number;
  activeSlots: number;
  totalEarned: string;
  redeemable: string;
}

export interface PublicStatsView {
  totalMembers: number;
  activeMembers: number;
  completedCycles: number;
  confirmedTransactions: number;
  distributed: string;
}

export interface IndexerStatusView {
  enabled: boolean;
  state?: 'idle' | 'running' | 'error';
  startBlock?: number;
  lastScannedBlock?: number;
  lastFinalizedBlock?: number;
  runCount?: number;
  lastError?: string | null;
  updatedAt?: string;
}

export interface AppDataSnapshot {
  member: MemberView | null;
  memberIds: string[];
  slots: SlotSummaryView[];
  /** Name kept: the on-chain slot views (every slot the contract activated, all cycles, all positions, from SeatFilled). */
  projectedSlots: ProjectedSlotView[];
  /** rules/rule13.txt: Leaders Club standing of the active member; null before slot 4 or in demo mode. */
  club: MemberClubView | null;
  /** rules/rule14.txt: the active member's Primary Token record. */
  primaryToken: PrimaryTokenView | null;
  activity: ActivityRecord[];
  chartData: number[];
  partners: PartnerView[];
  generationNodes: GenerationNodeView[];
  tokens: TokenView[];
  providers: ProviderViewModel[];
  ranks: RankViewModel[];
  rpcProviders: RpcProviderViewModel[];
  cycles: CycleView[];
  dashboardStats: DashboardStatsView;
  publicStats: PublicStatsView;
  indexer: IndexerStatusView;
  projectionNotice: string;
}

export const emptySnapshot: AppDataSnapshot = {
  member: null,
  memberIds: [],
  slots: [],
  projectedSlots: [],
  club: null,
  primaryToken: null,
  activity: [],
  chartData: Array.from({ length: 14 }, () => 0),
  partners: [],
  generationNodes: [],
  tokens: [],
  providers: [],
  ranks: [],
  rpcProviders: [],
  cycles: [],
  dashboardStats: {
    teamMembers: 0,
    directMembers: 0,
    completedCycles: 0,
    activeSlots: 0,
    totalEarned: '0',
    redeemable: '0',
  },
  publicStats: {
    totalMembers: 0,
    activeMembers: 0,
    completedCycles: 0,
    confirmedTransactions: 0,
    distributed: '0',
  },
  indexer: { enabled: false },
  projectionNotice: 'Connect and sign in to load the indexed on-chain data of your ID.',
};

